
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { localDraftService, type DraftData } from "@/services/LocalDraftService";
import { useLocalDraftList } from "@/hooks/useLocalDraftList";

// For backward compatibility with existing components
export type DraftCampaign = {
  id: string;
  campaign_name: string;
  created_at: string;
  updated_at: string;
  status: string;
};

export const useCampaignInit = () => {
  const [showNameDialog, setShowNameDialog] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { drafts: rawDrafts, loading, loadDrafts } = useLocalDraftList();

  // Convert DraftData to DraftCampaign format for backward compatibility
  const drafts: DraftCampaign[] = rawDrafts.map(draft => ({
    id: draft.id,
    campaign_name: draft.name,
    created_at: draft.createdAt,
    updated_at: draft.updatedAt,
    status: 'draft'
  }));

  // Handle creating a new campaign
  const handleCreateCampaign = useCallback(() => {
    console.log('Starting new campaign creation');
    
    // Clear all previous campaign data
    localDraftService.clearAllCampaignData();
    
    // Reset to step 0
    localStorage.setItem('currentStep', '0');
    
    console.log('All previous data cleared, showing name dialog');
    setShowNameDialog(true);
  }, []);

  // Handle campaign name submission and create draft
  const handleNameSubmit = useCallback(async (name: string) => {
    if (!name.trim()) {
      toast({
        title: "Invalid Name",
        description: "Please enter a valid campaign name.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('Creating campaign with name:', name);
      
      // Save campaign name to current session
      localStorage.setItem('campaignName', name);
      
      // Create new draft
      const draft = localDraftService.createDraft(name);
      console.log('Created draft:', draft.id);

      setShowNameDialog(false);

      toast({
        title: "Campaign Created",
        description: `Campaign "${name}" has been created successfully.`,
      });

      navigate('/campaign/choice');

    } catch (error) {
      console.error('Error creating campaign:', error);
      toast({
        title: "Error",
        description: "Failed to create campaign. Please try again.",
        variant: "destructive",
      });
    }
  }, [toast, navigate, loadDrafts]);

  return {
    loading,
    drafts,
    showNameDialog,
    setShowNameDialog,
    handleCreateCampaign,
    handleNameSubmit,
    loadDrafts
  };
};
